import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import { BaseForm, FormInput, FormMultiSelect } from "components/BaseForm";
import countryOptions from "components/Countries";
import { Spinner } from "@/components/ui/spinner";
import { NO_COUNTRY_OPTION } from "helpers/countries";
import RegionService from "services/Regions";

const regionCountryOptions = [NO_COUNTRY_OPTION, ...countryOptions];

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  countries: z.array(z.string()).min(1, "Select at least one country"),
});

/**
 * Region create/edit form. Pass `id` to edit — the region is fetched first so
 * the form mounts with its values already in `defaultValues`.
 */
const RegionForm = ({ id, onSuccess, onCancel }) => {
  const isEdit = !!id;

  const { data: region, isPending } = useQuery({
    queryKey: ["region", id],
    queryFn: () => RegionService.getRegion(id),
    enabled: isEdit,
  });

  const handleSubmit = async ({ name, countries }) => {
    const countryList = countries.join(",");
    if (isEdit) {
      await RegionService.editRegion(id, name, countryList);
    } else {
      await RegionService.createRegion(name, countryList);
    }
    toast.success(isEdit ? "Region updated" : "Region created");
  };

  if (isEdit && isPending) {
    return <Spinner />;
  }

  return (
    <BaseForm
      schema={schema}
      defaultValues={{
        name: region?.name ?? "",
        countries: region?.countries ?? [],
      }}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
      onCancel={onCancel}
      submitButtonText="Save"
    >
      <FormInput name="name" label="Name" required />
      <FormMultiSelect
        name="countries"
        label="Countries"
        placeholder="Select Country"
        options={regionCountryOptions}
        required
      />
    </BaseForm>
  );
};

export default RegionForm;
